import { nameToReferenceKey } from '@/vantage/components/evidence_table/helpers/fieldId.utilities';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SeeqNames } from '@seeqdev/constants';

export const MAX_POSTGRES_EPOCH_MILLISEC = 9223372036854; // Math.floor(9223372036854775807 / 1e6)

export const EVIDENCE_LIMIT = 80000; // Picked based on when the app started getting sluggish doing aggregation
export const EVIDENCE_CONTEXT_LIMIT = EVIDENCE_LIMIT * 10; // Each row can have several pieces of context

export const LONG_AGO = new Date('1980-01-01');

export const CONTEXT_FETCH_INTERVAL_MILLISECONDS = 10_000; // Periodically capture context changes by other users

export const CAPSULE_ID_TABLE_SEPARATOR = ':';
export const CAPSULE_ID_TREND_SEPARATOR = '_';

export const VANTAGE_ITEM_FINDER_EXCLUSIONS = [
  {
    operator: OperatorEnum.NOTEQUALS,
    propertyName: SeeqNames.Properties.DatasourceId,
    values: [{ value: 'Context Datum Storage' }],
  },
  {
    operator: OperatorEnum.NOTEQUALS,
    propertyName: SeeqNames.Properties.DatasourceId,
    values: [{ value: 'VantageSuppression' }],
  },
];

export const VantageNames = {
  Columns: {
    Asset: 'Asset',
    AssetUUID: 'Asset UUID',
    CapsuleID: 'Capsule ID',
    Condition: 'Condition',
    ConditionID: 'Condition ID',
    DatumID: SeeqNames.MaterializedTables.DatumIdColumn,
    DeletedAt: 'Deleted at',
    Duration: 'Duration',
    End: 'End',
    Flagged: SeeqNames.VantageColumns.Flagged,
    Formula: 'Formula',
    ItemsInGroup: 'groupitems',
    Kind: 'Kind',
    Labels: SeeqNames.VantageColumns.Labels,
    CapsuleCount: SeeqNames.VantageColumns.CapsuleCount,
    Mutable: 'Mutable',
    Path: 'Path',
    Properties: 'Properties',
    Reviewed: SeeqNames.VantageColumns.Reviewed,
    Start: 'Start',
    Suppressed: 'Suppressed',
    UpdatedAt: 'Updated At',
    CreatedAt: 'created at',
    TimeCreated: 'Time Created',
    TimeUpdated: 'Time Updated',
    IsUncertain: SeeqNames.CapsuleProperties.IsUncertain,
    CapsuleIdProperty: SeeqNames.CapsuleProperties.CapsuleIdProperty,
    LastCapsuleState: 'Last Capsule State',
  },
  GridColumnNames: {
    TimeUpdated: 'Time Updated',
    TimeCreated: 'Time Created',
  },
  Tables: {
    ContextLabels: 'context_labels',
    ContextComments: 'context_comments',
    ContextNumeric: 'context_numeric',
  },
  TableQueryContextColumns: {
    CreatorName: `creator_id.user.${SeeqNames.Properties.Name}`,
    // Comments
    ContextCommentsComment: 'context_comments.comment',
    ContextCommentsId: 'context_comments.id',
    ContextCommentsDatumId: 'context_comments.datum_id',
    ContextCommentsStartTime: 'context_comments.start_time',
    ContextCommentsEndTime: 'context_comments.end_time',
    ContextCommentsCreatedAt: 'context_comments.created_at',
    ContextCommentsUpdatedAt: 'context_comments.updated_at',
    ContextCommentsArchived: 'context_comments.archived',
    ContextCommentsCreatorId: 'context_comments.creator_id',
    ContextCommentsCreatorName: `context_comments.creator_id.user.${SeeqNames.Properties.Name}`,
    ContextCommentsCategoryId: 'context_comments.comment_category_id',
    ContextCommentsCategoryName: 'context_comments.comment_category_name',
    // Labels
    ContextLabelsCategoryId: 'context_labels.category_id',
    ContextLabelsEndTime: 'context_labels.end_time',
    ContextLabelsId: 'context_labels.id',
    ContextLabelsDatumId: 'context_labels.datum_id',
    ContextLabelsLabelId: 'context_labels.label_id',
    ContextLabelsLabelName: 'context_labels.label_name',
    ContextLabelsCategoryName: 'context_labels.category_name',
    ContextLabelsStartTime: 'context_labels.start_time',
    ContextLabelsCreatedAt: 'context_labels.created_at',
    ContextLabelsUpdatedAt: 'context_labels.updated_at',
    ContextLabelsArchived: 'context_labels.archived',
    ContextLabelsCreatorId: 'context_labels.creator_id',
    ContextLabelsCreatorName: `context_labels.creator_id.user.${SeeqNames.Properties.Name}`,
    // Numerics
    ContextNumericId: 'context_numeric.id',
    ContextNumericDatumId: 'context_numeric.datum_id',
    ContextNumericValue: 'context_numeric.value',
    ContextNumericCategoryId: 'context_numeric.numeric_description_id',
    ContextNumericCategoryName: 'context_numeric.numeric_description_name',
    ContextNumericStartTime: 'context_numeric.start_time',
    ContextNumericEndTime: 'context_numeric.end_time',
    ContextNumericCreatedAt: 'context_numeric.created_at',
    ContextNumericUpdatedAt: 'context_numeric.updated_at',
    ContextNumericArchived: 'context_numeric.archived',
    ContextNumericCreatorId: 'context_numeric.creator_id',
    ContextNumericCreatorName: `context_numeric.creator_id.user.${SeeqNames.Properties.Name}`,
    ContextOpaqueId: 'context_opaque.id',
    ItemType: `${SeeqNames.MaterializedTables.ItemIdColumn}.properties.${SeeqNames.Properties.Type}`,
  },
  ContextQueryContextColumns: {
    DatumId: 'datum_id',
    ContextGraphqlCommentsComment: 'context',
    ContextGraphqlLabelName: 'label',
    ContextGraphqlCategoryName: 'categoryName',
    ContextGraphqlNumericDescriptionName: 'numericDescriptionName',
    ContextGraphqlCommentCategoryName: 'commentCategoryName',
    ContextGraphqlNumericValue: 'value',
    ContextGraphqlContextId: 'contextId',
  },
  DerivedColumns: {
    Extinct: 'Extinct',
  },
  Extensions: {
    EditManualEvent: 'editManualEvent',
    Flag: 'flag',
    Labels: 'labels',
    Notes: 'notes',
    ReasonCodes: 'reason_codes',
    Reviewed: 'reviewed',
    Suppression: 'suppression',
    UserDefinedContext: 'userDefinedContext',
  },
  Kinds: {
    Asset: 'Asset',
    Capsule: 'Capsule',
    Condition: 'Condition',
  },
  ProxyKinds: {
    selected: 'selected',
    leaf: 'leaf',
    children: 'children',
  },
  SuppressionLabel: {
    ONGOING: 'ongoing',
    ENDED_BY_USER: 'ended-by-user',
  },
  DateRangeModalView: {
    Standard: 'standard',
    Fixed: 'fixed',
    Advanced: 'advanced',
  },
  ColumnValues: {
    Extinct: 'EXTINCT',
  },
  ContextFormattingFields: {
    TimeUpdated: 'timeUpdated',
    Archived: 'archived',
    EventEnd: 'eventEnd',
    EventStart: 'eventStart',
  },
  ReasonCodes: {
    MaterializedTableColumnName: 'Reason codes',
    LabelCategory: `${SeeqNames.Types.Vantage}.reason_codes`,
  },
  UserDefinedContext: {
    ContextNamePrefix: SeeqNames.SeeqContextNameSpace.UserDefinedContextNamespace,
  },
};

const REASON_CODES_EXTENSION_SEPARATOR = '::';

export const isReasonCodesExtensionName = (name?: string): boolean =>
  name === VantageNames.Extensions.ReasonCodes ||
  !!name?.startsWith(`${VantageNames.Extensions.ReasonCodes}${REASON_CODES_EXTENSION_SEPARATOR}`);

export const reasonCodesExtensionName = (hierarchyCategoryId: string): string =>
  `${VantageNames.Extensions.ReasonCodes}${REASON_CODES_EXTENSION_SEPARATOR}${hierarchyCategoryId}`;

export const reasonCodesHierarchyIdFromName = (name?: string): string | undefined => {
  const prefix = `${VantageNames.Extensions.ReasonCodes}${REASON_CODES_EXTENSION_SEPARATOR}`;
  return name?.startsWith(prefix) ? name.slice(prefix.length) : undefined;
};

export const GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_LABELS = [
  { uuidColumn: SeeqNames.MaterializedTables.ItemIdColumn, propertyNames: [SeeqNames.Properties.Type] },
  {
    uuidColumn: VantageNames.TableQueryContextColumns.ContextLabelsCreatorId,
    propertyNames: [SeeqNames.Properties.Name],
  },
];

export const GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_NUMERICS = [
  { uuidColumn: SeeqNames.MaterializedTables.ItemIdColumn, propertyNames: [SeeqNames.Properties.Type] },
  {
    uuidColumn: VantageNames.TableQueryContextColumns.ContextNumericCreatorId,
    propertyNames: [SeeqNames.Properties.Name],
  },
];

export const GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_COMMENTS = [
  { uuidColumn: SeeqNames.MaterializedTables.ItemIdColumn, propertyNames: [SeeqNames.Properties.Type] },
  {
    uuidColumn: VantageNames.TableQueryContextColumns.ContextCommentsCreatorId,
    propertyNames: [SeeqNames.Properties.Name],
  },
];

// Suppression isn't a virtual column (its values are stored in the MT)
export const VIRTUAL_COLUMNS = [
  { name: VantageNames.DerivedColumns.Extinct, onlyWhenGrouping: false },
  { name: VantageNames.Columns.Reviewed, onlyWhenGrouping: false },
  { name: VantageNames.Columns.Flagged, onlyWhenGrouping: false },
  { name: VantageNames.Columns.Labels, onlyWhenGrouping: false },
  { name: VantageNames.Columns.CapsuleCount, onlyWhenGrouping: true },
] as const;

export const ColumnToDerivedColumn: Record<string, string> = {
  [VantageNames.Columns.LastCapsuleState]: VantageNames.DerivedColumns.Extinct,
};

export const configUneditableColumns = [
  SeeqNames.MaterializedTables.ItemIdColumn,
  SeeqNames.CapsuleProperties.ConditionId,
  SeeqNames.MaterializedTables.DatumIdColumn,
  ...Object.values(VantageNames.Columns).filter(
    (column) => column !== VantageNames.Columns.Asset && column !== VantageNames.Columns.Path,
  ),
];

export const configHiddenColumns = [
  SeeqNames.MaterializedTables.ItemIdColumn,
  SeeqNames.MaterializedTables.DatumIdColumn,
  SeeqNames.CapsuleProperties.ConditionId,
  VantageNames.Columns.AssetUUID,
  VantageNames.Columns.UpdatedAt,
  VantageNames.Columns.DeletedAt,
  VantageNames.Columns.Kind,
  VantageNames.Columns.Mutable,
  VantageNames.Columns.Properties,
  VantageNames.Columns.Suppressed,
];

export const defaultVisibleColumns = [
  nameToReferenceKey(VantageNames.Columns.Condition),
  VantageNames.Columns.Asset,
  VantageNames.Columns.Start,
  VantageNames.Columns.End,
  VantageNames.Columns.Duration,
  VantageNames.Columns.Flagged,
  VantageNames.Columns.Reviewed,
  VantageNames.Columns.Labels,
  VantageNames.Columns.Suppressed,
  VantageNames.Columns.ItemsInGroup,
];

export const PROTECTED_CATEGORIES = [
  VantageNames.Extensions.Flag,
  VantageNames.Extensions.Labels,
  VantageNames.Extensions.Reviewed,
  VantageNames.Extensions.Suppression,
];

export const QuickTrendSignalColors = {
  light: [
    '#BA55D3', // medium orchid
    '#228B22', // forest green
    '#0000FF', // blue
    '#8B008B', // dark magenta
    '#FF8C00', // dark orange
    '#49c164', // light green
    '#2F4F4F', // dark slate gray
    '#9932CC', // dark orchid
    '#008B8B', // dark cyan
    '#B8860B', // dark goldenrod
  ],
  dark: [
    '#DDA0DD', // lighter plum (complementary to medium orchid)
    '#90EE90', // light green (complementary to forest green)
    '#87CEEB', // sky blue (complementary to blue)
    '#DA70D6', // orchid (complementary to dark magenta)
    '#FFB347', // peach (complementary to dark orange)
    '#98FB98', // pale green (complementary to light green)
    '#708090', // slate gray (complementary to dark slate gray)
    '#BA55D3', // medium orchid (complementary to dark orchid)
    '#40E0D0', // turquoise (complementary to dark cyan)
    '#FFD700', // gold
  ],
};

export const QuickTrendCapsuleColors = {
  light: [
    '#4B0082', // deep indigo
    '#006400', // dark green
    '#0066CC', // dodger blue
    '#C71585', // medium violet red
    '#FFA500', // bright orange
    '#72ce87', // light green
    '#4682B4', // steel blue
    '#BA55D3', // medium orchid
    '#20B2AA', // light sea green
    '#DC143C', // crimson
    '#8B4513', // saddle brown
    '#483D8B', // dark slate blue
    '#2E8B57', // sea green
    '#FF6347', // tomato
    '#4169E1', // royal blue
    '#D2691E', // chocolate
    '#9370DB', // medium purple
    '#008080', // teal
    '#CD5C5C', // indian red
    '#556B2F', // dark olive green
  ],
  dark: [
    '#C8A2C8', // light lavender (high contrast complementary to deep indigo)
    '#90EE90', // light green (complementary to dark green)
    '#87CEEB', // sky blue (complementary to dodger blue)
    '#DA70D6', // orchid (complementary to medium violet red)
    '#FFB347', // peach (complementary to bright orange)
    '#98FB98', // pale green (complementary to light green)
    '#87CEFA', // light sky blue (complementary to steel blue)
    '#DDA0DD', // plum (complementary to medium orchid)
    '#40E0D0', // turquoise (complementary to light sea green)
    '#F08080', // light coral (complementary to crimson)
    '#D2B48C', // tan (complementary to saddle brown)
    '#9DB4FF', // light periwinkle (complementary to dark slate blue)
    '#90EE90', // light green (complementary to sea green)
    '#FFA07A', // light salmon (complementary to tomato)
    '#6495ED', // cornflower blue (complementary to royal blue)
    '#F4A460', // sandy brown (complementary to chocolate)
    '#B19CD9', // light purple (complementary to medium purple)
    '#5F9EA0', // cadet blue (complementary to teal)
    '#E9967A', // dark salmon (complementary to indian red)
    '#9ACD32', // yellow green (complementary to dark olive green)
  ],
};
